import {Record} from 'immutable';

import {match, makeReducer} from 'web-app/util/redux';
import * as DraftActions from 'web-app/react/entities/drafts/actions';
import type {ObservationVariant} from 'web-app/util/graphql/api-types';

import * as Actions from './actions';

export interface DraftManagerStateProps {
    draftInFlight: boolean;
    updatingDraft: boolean;
    updateFailed: boolean;
    creating: boolean;
    draftIsDirty: boolean;
    clearing: boolean;
    posting: boolean;
    showDraftSelector: boolean;
    showObservationForm: boolean;
    observationVariant: ObservationVariant | null;
    currentDraftId: string | null;
}

const defaultState: DraftManagerStateProps = {
    draftInFlight: false,
    updatingDraft: false,
    updateFailed: false,
    creating: false,
    draftIsDirty: false,
    clearing: false,
    posting: false,
    showDraftSelector: false,
    showObservationForm: false,
    observationVariant: null,
    currentDraftId: null,
};

export class DraftManagerState extends Record(defaultState) implements DraftManagerStateProps {}

export default makeReducer<DraftManagerState>(
    [
        match(Actions.reset, () => new DraftManagerState()),

        match(Actions.showDraftSelector, (state, {payload}) =>
            state.merge({
                showDraftSelector: payload.show,
            }),
        ),

        match(Actions.showObservationForm, (state, {payload}) =>
            state.merge({
                showObservationForm: payload.show,
                observationVariant: null,
                currentDraftId: null,
            }),
        ),

        match(Actions.clearDraft, state =>
            state.merge({
                posting: false,
                clearing: true,
                showDraftSelector: false,
            }),
        ),

        match(Actions.clearDraftSuccess, (state, {payload}) =>
            state.merge({
                showObservationForm: payload.showObservationForm,
                draftIsDirty: false,
                clearing: false,
                updateFailed: false,
                currentDraftId: null,
                observationVariant: null,
            }),
        ),

        match(Actions.clearDraftFailed, state =>
            state.merge({
                clearing: false,
                updateFailed: true,
            }),
        ),

        match(Actions.selectDraft, state =>
            state.merge({
                posting: false,
                draftIsDirty: false,
                updateFailed: false,
                updatingDraft: true,
                showDraftSelector: false,
            }),
        ),

        match(Actions.selectDraftSuccess, (state, {payload}) =>
            state.merge({
                updatingDraft: false,
                showObservationForm: payload.showObservationForm,
                observationVariant: payload.variant,
                currentDraftId: payload.draftId,
            }),
        ),

        match(DraftActions.create, state =>
            state.merge({
                draftInFlight: true,
                updateFailed: false,
                creating: true,
            }),
        ),

        match(DraftActions.createFailed, state =>
            state.merge({
                draftInFlight: false,
                updateFailed: true,
                creating: false,
            }),
        ),

        match(DraftActions.createSuccess, (state, {payload}) =>
            state.merge({
                draftInFlight: false,
                draftIsDirty: false,
                creating: false,
                observationVariant: payload.draft.variant,
                currentDraftId: payload.draft.postId ?? payload.draft.draftId,
            }),
        ),

        match(Actions.update, state =>
            state.merge({
                draftInFlight: true,
                updateFailed: false,
            }),
        ),

        match(Actions.send, state =>
            state.merge({
                posting: true,
            }),
        ),

        match(Actions.sendFailed, state =>
            state.merge({
                posting: false,
            }),
        ),

        match(Actions.sendAborted, state =>
            state.merge({
                posting: false,
            }),
        ),

        match(Actions.updatePost, state =>
            state.merge({
                posting: true,
            }),
        ),

        match(Actions.updatePostSuccess, state =>
            state.merge({
                posting: false,
            }),
        ),

        match(Actions.updatePostFailed, state =>
            state.merge({
                posting: false,
            }),
        ),

        match(DraftActions.sendSuccess, state =>
            state.merge({
                posting: false,
                draftIsDirty: false,
                updateFailed: false,
                observationVariant: null,
                currentDraftId: null,
            }),
        ),

        match(DraftActions.updateSuccess, state =>
            state.merge({
                draftInFlight: false,
                draftIsDirty: false,
                updateFailed: false,
            }),
        ),

        match(Actions.createOrUpdateFailed, state =>
            state.merge({
                draftInFlight: false,
                creating: false,
                updateFailed: true,
            }),
        ),

        match(DraftActions.removeSuccess, (state, {payload}) =>
            state.merge({
                updateFailed: false,
                draftIsDirty: false,
                observationVariant: state.currentDraftId === payload.draftId ? null : state.observationVariant,
                currentDraftId: state.currentDraftId === payload.draftId ? null : state.currentDraftId,
            }),
        ),

        match(DraftActions.removeAborted, state =>
            state.merge({
                showDraftSelector: true,
            }),
        ),

        match(Actions.createOrUpdate, state =>
            state.merge({
                draftIsDirty: true,
            }),
        ),
    ],
    new DraftManagerState(),
);
